import React from 'react';
import { X } from 'lucide-react';
import type { PlaceFilter, MoodLabel, ActivityLabel, CommunityFeature } from '../../types';
import { MOODS, ACTIVITIES, COMMUNITY_FEATURES } from '../../constants';

interface FilterPanelProps {
  isOpen: boolean;
  filters: PlaceFilter;
  filteredCount: number;
  onClose: () => void;
  onFiltersChange: (filters: Partial<PlaceFilter>) => void;
  onReset: () => void;
}

export function FilterPanel({
  isOpen,
  filters,
  filteredCount,
  onClose,
  onFiltersChange,
  onReset,
}: FilterPanelProps) {
  if (!isOpen) return null;

  const toggleMood = (mood: MoodLabel) => {
    const newMoods = filters.moods.includes(mood)
      ? filters.moods.filter((m) => m !== mood)
      : [...filters.moods, mood];
    onFiltersChange({ moods: newMoods });
  };

  const toggleActivity = (activity: ActivityLabel) => {
    const newActivities = filters.activities.includes(activity)
      ? filters.activities.filter((a) => a !== activity)
      : [...filters.activities, activity];
    onFiltersChange({ activities: newActivities });
  };

  const toggleCommunity = (feature: CommunityFeature) => {
    const newCommunity = filters.community.includes(feature)
      ? filters.community.filter((c) => c !== feature)
      : [...filters.community, feature];
    onFiltersChange({ community: newCommunity });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-fade-in">
      <div className="bg-white w-full max-w-lg rounded-t-3xl max-h-[85vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">Filter</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Mood */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Atmosphäre & Stimmung</h3>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((mood) => (
                <button
                  key={mood.label}
                  className={`px-3 py-2 rounded-lg text-sm transition ${
                    filters.moods.includes(mood.label)
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => toggleMood(mood.label)}
                >
                  {mood.icon} {mood.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activities */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Aktivitäten</h3>
            <div className="flex flex-wrap gap-2">
              {ACTIVITIES.map((activity) => (
                <button
                  key={activity.label}
                  className={`px-3 py-2 rounded-lg text-sm transition ${
                    filters.activities.includes(activity.label)
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => toggleActivity(activity.label)}
                >
                  {activity.icon} {activity.label}
                </button>
              ))}
            </div>
          </div>

          {/* Community Features */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Gemeinschaft & Features</h3>
            <div className="flex flex-wrap gap-2">
              {COMMUNITY_FEATURES.map((feature) => (
                <button
                  key={feature.label}
                  className={`px-3 py-2 rounded-lg text-sm transition ${
                    filters.community.includes(feature.label)
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => toggleCommunity(feature.label)}
                >
                  {feature.icon} {feature.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-4 border-t flex gap-3">
          <button
            className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            onClick={onReset}
          >
            Zurücksetzen
          </button>
          <button
            className="flex-1 py-3 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors"
            onClick={onClose}
          >
            {filteredCount} Orte anzeigen
          </button>
        </div>
      </div>
    </div>
  );
}
